import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { CartrackVehicle, FleetRouteLayer, FleetRouteStop } from '../../lib/cartrack';
import { formatCartrackSpeed, formatRouteTimeOnly } from '../../lib/cartrack';
import { fleetTasksForStop, type FleetTaskGeoCoords } from '../../lib/fleetTasks';
import { taskAccountDisplay } from '../../lib/taskAccountDisplay';
import { clientById } from '../../utils/crmHelpers';
import { addGoogleFleetBasemap, getGoogleMapsApiKey } from '../../lib/googleMaps';
import { getLeafletRasterBasemapOptions } from '../../lib/leafletRasterBasemap';
import type { Client, Task } from '../../types/crm';

/** Keep pan/zoom inside Singapore (+ small buffer for coast / Changi / Tuas). */
const FLEET_SINGAPORE_BOUNDS = L.latLngBounds([1.144, 103.605], [1.494, 104.124]);
const FLEET_MIN_ZOOM = 11;
const FLEET_MAX_ZOOM = 18;

type FleetMapProps = {
  vehicles: CartrackVehicle[];
  selectedRegistration: string | null;
  onSelectVehicle?: (registration: string) => void;
  routeLayers: FleetRouteLayer[];
  routeStops?: FleetRouteStop[];
  routeDate?: string;
  tasks?: Task[];
  clients?: Client[];
  taskGeoCoords?: FleetTaskGeoCoords;
  showLiveVehicles?: boolean;
  canManageTasks?: boolean;
  onStopAddTask?: (stop: FleetRouteStop) => void;
  onStopAssignTask?: (stop: FleetRouteStop) => void;
  onStopTaskClick?: (task: Task) => void;
  onMapBusyChange?: (busy: boolean) => void;
  className?: string;
};

function escapeHtml(value: string): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function fleetBubblePopup(title: string, lines: string[], actions?: string): string {
  return `<div class="fleet-map-bubble">
    <div class="fleet-map-bubble__title">${escapeHtml(title)}</div>
    ${lines.map((line) => `<div class="fleet-map-bubble__line">${escapeHtml(line)}</div>`).join('')}
    ${actions ?? ''}
  </div>`;
}

function fleetStopTaskLabel(task: Task, clients: Client[]): string {
  const client = clientById(clients, task.client_id);
  const account = taskAccountDisplay(task, client);
  const accountName = account.name.trim();
  const title = task.title.trim();
  if (accountName && accountName !== 'No account') {
    return `${accountName} · ${title}`;
  }
  return title || 'Task';
}

function fleetStopTasksHtml(tasks: Task[], clients: Client[]): string {
  if (tasks.length === 0) {
    return '<div class="fleet-map-bubble__tasks fleet-map-bubble__tasks--empty">No tasks linked to this stop yet.</div>';
  }
  return `<div class="fleet-map-bubble__tasks">
    <div class="fleet-map-bubble__tasks-title">Tasks (${tasks.length})</div>
    <ul class="fleet-map-bubble__task-list">
      ${tasks
        .map((task) => {
          const done = (task.status ?? '').trim().toLowerCase() === 'done'
            || (task.status ?? '').trim().toLowerCase() === 'completed';
          return `<li>
            <button
              type="button"
              class="fleet-map-bubble__task-btn${done ? ' fleet-map-bubble__task-item--done' : ''}"
              data-fleet-stop-task-id="${escapeHtml(String(task.id))}"
            >
              <span class="fleet-map-bubble__task-title">${escapeHtml(fleetStopTaskLabel(task, clients))}</span>
              ${task.status ? `<span class="fleet-map-bubble__task-status">${escapeHtml(task.status)}</span>` : ''}
            </button>
          </li>`;
        })
        .join('')}
    </ul>
  </div>`;
}

function fleetStopActionsHtml(): string {
  return `<div class="fleet-map-bubble__actions">
    <button type="button" class="fleet-map-bubble__btn" data-fleet-stop-action="add">+ Add task</button>
    <button type="button" class="fleet-map-bubble__btn fleet-map-bubble__btn--secondary" data-fleet-stop-action="assign">Assign task</button>
  </div>`;
}

const POPUP_OPTIONS: L.PopupOptions = {
  className: 'fleet-map-popup',
  maxWidth: 340,
  minWidth: 200,
  autoPan: true,
  autoPanPadding: [56, 56],
  keepInView: true,
  closeButton: true,
};

const VEHICLE_ICON_WIDTH = 220;
const VEHICLE_ICON_HEIGHT = 92;
const GPS_CLUSTER_METERS = 10;

function vehicleLabelHtml(vehicle: CartrackVehicle, selected: boolean, showBubble: boolean): string {
  const tone = selected ? 'fleet-map-vehicle-label--selected' : 'fleet-map-vehicle-label--default';
  const registration = vehicle.registration.replace(/\s+/g, '').toUpperCase();
  const meta = `${vehicle.driverName || 'No driver'} · ${formatCartrackSpeed(vehicle.speedKmh)}`;
  const bubble = showBubble
    ? `<div class="fleet-map-vehicle-label__bubble" data-fleet-vehicle-bubble>
        <div class="fleet-map-vehicle-label__box">
          <div class="fleet-map-vehicle-label__reg">${escapeHtml(registration)}</div>
          <div class="fleet-map-vehicle-label__meta">${escapeHtml(meta)}</div>
        </div>
        <div class="fleet-map-vehicle-label__tail" aria-hidden="true"></div>
      </div>`
    : '';
  return `<div class="fleet-map-vehicle-label ${tone}${showBubble ? '' : ' fleet-map-vehicle-label--dot-only'}" data-fleet-vehicle-label>
    <div class="fleet-map-vehicle-label__canvas">
      ${bubble}
      <div class="fleet-map-vehicle-label__dot" data-fleet-vehicle-dot aria-hidden="true"></div>
    </div>
  </div>`;
}

function vehicleMarkerIcon(vehicle: CartrackVehicle, selected: boolean, showBubble: boolean): L.DivIcon {
  return L.divIcon({
    className: [
      'fleet-map-vehicle-marker',
      selected ? 'fleet-map-vehicle-marker--selected' : '',
      showBubble ? '' : 'fleet-map-vehicle-marker--dot-only',
    ]
      .filter(Boolean)
      .join(' '),
    html: vehicleLabelHtml(vehicle, selected, showBubble),
    iconSize: [VEHICLE_ICON_WIDTH, VEHICLE_ICON_HEIGHT],
    iconAnchor: [VEHICLE_ICON_WIDTH / 2, VEHICLE_ICON_HEIGHT],
    popupAnchor: [0, -VEHICLE_ICON_HEIGHT - 6],
  });
}

const CLUSTER_HIT_SIZE = 40;

function clusterHitIcon(count: number): L.DivIcon {
  return L.divIcon({
    className: 'fleet-map-cluster-hit',
    html: `<div class="fleet-map-cluster-hit__area" aria-label="${count} vehicles stacked. Tap to switch.">
      <span class="fleet-map-cluster-hit__badge" aria-hidden="true">${count}</span>
    </div>`,
    iconSize: [CLUSTER_HIT_SIZE, CLUSTER_HIT_SIZE],
    iconAnchor: [CLUSTER_HIT_SIZE / 2, CLUSTER_HIT_SIZE / 2],
  });
}

function gpsDistanceMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const earthRadiusM = 6371000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadiusM * Math.asin(Math.sqrt(a));
}

function buildGpsClusters(vehicles: CartrackVehicle[], thresholdMeters: number): number[][] {
  const parent = vehicles.map((_, index) => index);

  const find = (index: number): number => {
    if (parent[index] !== index) parent[index] = find(parent[index]!);
    return parent[index]!;
  };

  const union = (a: number, b: number) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) parent[rootB] = rootA;
  };

  for (let i = 0; i < vehicles.length; i += 1) {
    const a = vehicles[i]!;
    for (let j = i + 1; j < vehicles.length; j += 1) {
      const b = vehicles[j]!;
      if (gpsDistanceMeters(a.lat!, a.lng!, b.lat!, b.lng!) <= thresholdMeters) {
        union(i, j);
      }
    }
  }

  const groups = new Map<number, number[]>();
  vehicles.forEach((_, index) => {
    const root = find(index);
    const bucket = groups.get(root) ?? [];
    bucket.push(index);
    groups.set(root, bucket);
  });

  return [...groups.values()];
}

function nextClusterSelection(
  members: CartrackVehicle[],
  selectedRegistration: string | null,
): string {
  const currentIndex = members.findIndex((vehicle) => vehicle.registration === selectedRegistration);
  const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % members.length : 0;
  return members[nextIndex]!.registration;
}

function stopPinSvg(label: string): string {
  return `<svg viewBox="0 0 32 40" width="30" height="38" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M16 1.5C9.65 1.5 4.5 6.65 4.5 13c0 7.8 11.5 24.5 11.5 24.5S27.5 20.8 27.5 13C27.5 6.65 22.35 1.5 16 1.5z" fill="#f59e0b" stroke="#b45309" stroke-width="1.5"/>
    <circle cx="16" cy="13" r="7.5" fill="#ffffff"/>
    <text x="16" y="13.5" fill="#b45309" font-size="9" font-family="sans-serif" font-weight="bold" text-anchor="middle" dominant-baseline="middle">${label}</text>
  </svg>`;
}

function endpointPinSvg(label: string, fill: string, stroke: string): string {
  return `<svg viewBox="0 0 32 40" width="28" height="35" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M16 1.5C9.65 1.5 4.5 6.65 4.5 13c0 7.8 11.5 24.5 11.5 24.5S27.5 20.8 27.5 13C27.5 6.65 22.35 1.5 16 1.5z" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>
    <circle cx="16" cy="13" r="7.5" fill="#ffffff"/>
    <text x="16" y="13.5" fill="${stroke}" font-size="9" font-family="sans-serif" font-weight="bold" text-anchor="middle" dominant-baseline="middle">${label}</text>
  </svg>`;
}

function routeArrowSvg(rotationDeg: number, estimated: boolean): string {
  const color = estimated ? '#e57373' : '#c41e3a';
  return `<div class="fleet-map-arrow" style="transform: rotate(${rotationDeg}deg); transform-origin: center center;">
    <span class="fleet-map-arrow__chevrons" style="color:${color}">&gt;&gt;&gt;</span>
  </div>`;
}

function stopMarkerIcon(label: string): L.DivIcon {
  return L.divIcon({
    className: 'fleet-map-pin fleet-map-pin--stop',
    html: stopPinSvg(label),
    iconSize: [30, 38],
    iconAnchor: [15, 38],
    popupAnchor: [0, -42],
  });
}

function endpointMarkerIcon(label: string, fill: string, stroke: string): L.DivIcon {
  return L.divIcon({
    className: 'fleet-map-pin fleet-map-pin--endpoint',
    html: endpointPinSvg(label, fill, stroke),
    iconSize: [28, 35],
    iconAnchor: [14, 35],
    popupAnchor: [0, -38],
  });
}

function routeArrowIcon(rotationDeg: number, estimated: boolean): L.DivIcon {
  return L.divIcon({
    className: 'fleet-map-arrow-wrap',
    html: routeArrowSvg(rotationDeg, estimated),
    iconSize: [30, 14],
    iconAnchor: [15, 7],
  });
}

function screenBearingDeg(map: L.Map, from: L.LatLngTuple, to: L.LatLngTuple): number {
  const p1 = map.latLngToLayerPoint(L.latLng(from[0], from[1]));
  const p2 = map.latLngToLayerPoint(L.latLng(to[0], to[1]));
  return (Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180) / Math.PI;
}

function addRouteArrows(
  map: L.Map,
  points: L.LatLngTuple[],
  estimated: boolean,
  storage: L.Marker[],
): void {
  if (points.length < 2) return;

  const targetArrows = Math.min(18, Math.max(4, Math.floor(points.length / 8)));
  const step = Math.max(2, Math.floor(points.length / targetArrows));

  for (let i = step; i < points.length; i += step) {
    const back = Math.max(0, i - 2);
    const ahead = Math.min(points.length - 1, i + 1);
    const from = points[back]!;
    const to = points[ahead]!;
    const rotation = screenBearingDeg(map, from, to);
    const mid: L.LatLngTuple = [
      (points[i - 1]![0] + points[i]![0]) / 2,
      (points[i - 1]![1] + points[i]![1]) / 2,
    ];

    const marker = L.marker(mid, {
      pane: 'fleetArrowPane',
      icon: routeArrowIcon(rotation, estimated),
      interactive: false,
      keyboard: false,
    }).addTo(map);
    storage.push(marker);
  }
}

function bindFleetPopup(marker: L.Marker, title: string, lines: string[]): void {
  marker.bindPopup(fleetBubblePopup(title, lines), POPUP_OPTIONS);
  marker.on('popupopen', () => {
    marker.setZIndexOffset(2000);
  });
  marker.on('popupclose', () => {
    marker.setZIndexOffset(0);
  });
}

function bindFleetStopPopup(
  marker: L.Marker,
  stopIndex: number,
  lines: string[],
  assignedTasks: Task[],
  allTasks: Task[],
  allClients: Client[],
  canManageTasks: boolean,
  onAddTask?: () => void,
  onAssignTask?: () => void,
  onTaskClick?: (task: Task) => void,
): void {
  const footer = `${fleetStopTasksHtml(assignedTasks, allClients)}${canManageTasks ? fleetStopActionsHtml() : ''}`;
  marker.bindPopup(fleetBubblePopup(`Stop ${stopIndex}`, lines, footer), POPUP_OPTIONS);
  marker.on('popupopen', () => {
    marker.setZIndexOffset(2000);
    const root = marker.getPopup()?.getElement();
    if (!root) return;

    root.querySelectorAll<HTMLButtonElement>('[data-fleet-stop-task-id]').forEach((btn) => {
      const taskId = btn.getAttribute('data-fleet-stop-task-id');
      if (!taskId) return;
      btn.addEventListener(
        'click',
        (event) => {
          L.DomEvent.stopPropagation(event);
          L.DomEvent.preventDefault(event);
          marker.closePopup();
          const task =
            allTasks.find((item) => String(item.id) === taskId) ??
            assignedTasks.find((item) => String(item.id) === taskId);
          if (task) onTaskClick?.(task);
        },
        { once: true },
      );
    });

    if (!canManageTasks) return;
    const addBtn = root.querySelector<HTMLButtonElement>('[data-fleet-stop-action="add"]');
    const assignBtn = root.querySelector<HTMLButtonElement>('[data-fleet-stop-action="assign"]');
    addBtn?.addEventListener(
      'click',
      (event) => {
        L.DomEvent.stopPropagation(event);
        L.DomEvent.preventDefault(event);
        marker.closePopup();
        onAddTask?.();
      },
      { once: true },
    );
    assignBtn?.addEventListener(
      'click',
      (event) => {
        L.DomEvent.stopPropagation(event);
        L.DomEvent.preventDefault(event);
        marker.closePopup();
        onAssignTask?.();
      },
      { once: true },
    );
  });
  marker.on('popupclose', () => {
    marker.setZIndexOffset(600);
  });
}

function clearVehicleLayers(map: L.Map, layers: ReturnType<typeof createEmptyLayers>): void {
  layers.markers.forEach((marker) => map.removeLayer(marker));
  layers.markers = [];
  layers.clusterHits.forEach((marker) => map.removeLayer(marker));
  layers.clusterHits = [];
}

function createEmptyLayers() {
  return {
    markers: [] as L.Marker[],
    clusterHits: [] as L.Marker[],
    routes: [] as L.Polyline[],
    routeArrows: [] as L.Marker[],
    stopMarkers: [] as L.Marker[],
    start: undefined as L.Marker | undefined,
    end: undefined as L.Marker | undefined,
    parked: undefined as L.Circle | undefined,
  };
}

function renderVehicleLayers(
  map: L.Map,
  layers: ReturnType<typeof createEmptyLayers>,
  vehicles: CartrackVehicle[],
  selectedRegistration: string | null,
  showLiveVehicles: boolean,
  onSelectVehicle?: (registration: string) => void,
): void {
  clearVehicleLayers(map, layers);

  const plotted = showLiveVehicles
    ? vehicles.filter((vehicle) => vehicle.lat != null && vehicle.lng != null)
    : [];
  const plottedMarkers: Array<{ vehicle: CartrackVehicle; marker: L.Marker }> = [];
  const clusters = buildGpsClusters(plotted, GPS_CLUSTER_METERS);
  const clusterByIndex = new Map<number, number[]>();
  clusters.forEach((cluster) => {
    cluster.forEach((index) => clusterByIndex.set(index, cluster));
  });

  plotted.forEach((vehicle, index) => {
    const selected = selectedRegistration === vehicle.registration;
    const cluster = clusterByIndex.get(index) ?? [index];
    const showBubble = cluster.length === 1 || selected;
    const stacked = cluster.length > 1;

    const marker = L.marker([vehicle.lat!, vehicle.lng!], {
      pane: 'fleetMarkerPane',
      icon: vehicleMarkerIcon(vehicle, selected, showBubble),
      // Single vehicles handle their own clicks; stacks use the cluster hit target.
      interactive: !stacked,
      zIndexOffset: selected ? 1200 : 0,
    }).addTo(map);
    if (!stacked) {
      marker.on('click', () => onSelectVehicle?.(vehicle.registration));
    }
    layers.markers.push(marker);
    plottedMarkers.push({ vehicle, marker });
  });

  clusters.forEach((clusterIndexes) => {
    if (clusterIndexes.length < 2) return;

    const members = clusterIndexes.map((index) => plottedMarkers[index]!);
    const vehiclesInCluster = members.map((member) => member.vehicle);
    const anchorVehicle =
      vehiclesInCluster.find((vehicle) => vehicle.registration === selectedRegistration) ??
      vehiclesInCluster[0]!;
    const hitLatLng = L.latLng(anchorVehicle.lat!, anchorVehicle.lng!);
    const clusterSelected = vehiclesInCluster.some(
      (vehicle) => vehicle.registration === selectedRegistration,
    );

    const hitMarker = L.marker(hitLatLng, {
      pane: 'fleetMarkerPane',
      icon: clusterHitIcon(vehiclesInCluster.length),
      zIndexOffset: clusterSelected ? 1800 : 1600,
    }).addTo(map);

    hitMarker.on('click', () => {
      onSelectVehicle?.(nextClusterSelection(vehiclesInCluster, selectedRegistration));
    });

    layers.clusterHits.push(hitMarker);
  });
}

export function FleetMap({
  vehicles,
  selectedRegistration,
  onSelectVehicle,
  routeLayers,
  routeStops = [],
  routeDate = '',
  tasks = [],
  clients = [],
  taskGeoCoords = {},
  showLiveVehicles = true,
  canManageTasks = false,
  onStopAddTask,
  onStopAssignTask,
  onStopTaskClick,
  onMapBusyChange,
  className = '',
}: FleetMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const [basemapKind, setBasemapKind] = useState<'google' | 'raster' | 'loading'>('loading');
  const [basemapError, setBasemapError] = useState<string | null>(null);
  const layersRef = useRef(createEmptyLayers());
  const lastFitKeyRef = useRef('');
  const vehiclesRef = useRef(vehicles);
  vehiclesRef.current = vehicles;
  const onSelectVehicleRef = useRef(onSelectVehicle);
  const onStopAddTaskRef = useRef(onStopAddTask);
  const onStopAssignTaskRef = useRef(onStopAssignTask);
  const onStopTaskClickRef = useRef(onStopTaskClick);
  const onMapBusyChangeRef = useRef(onMapBusyChange);
  onSelectVehicleRef.current = onSelectVehicle;
  onStopAddTaskRef.current = onStopAddTask;
  onStopAssignTaskRef.current = onStopAssignTask;
  onStopTaskClickRef.current = onStopTaskClick;
  onMapBusyChangeRef.current = onMapBusyChange;

  // Fit once when live positions first appear — not on every GPS poll.
  const liveFleetReady = showLiveVehicles && vehicles.some((v) => v.lat != null && v.lng != null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    let cancelled = false;
    const container = containerRef.current;

    void (async () => {
      const map = L.map(container, {
        zoomControl: false,
        minZoom: FLEET_MIN_ZOOM,
        maxZoom: FLEET_MAX_ZOOM,
        maxBounds: FLEET_SINGAPORE_BOUNDS,
        maxBoundsViscosity: 1,
        worldCopyJump: false,
      }).setView([1.3521, 103.8198], 12, { animate: false });

      map.createPane('fleetRoutePane');
      const routePane = map.getPane('fleetRoutePane');
      if (routePane) routePane.style.zIndex = '640';

      map.createPane('fleetArrowPane');
      const arrowPane = map.getPane('fleetArrowPane');
      if (arrowPane) arrowPane.style.zIndex = '645';

      map.createPane('fleetMarkerPane');
      const markerPane = map.getPane('fleetMarkerPane');
      if (markerPane) markerPane.style.zIndex = '660';

      const popupPane = map.getPane('popupPane');
      if (popupPane) popupPane.style.zIndex = '700';

      map.on('popupopen', () => onMapBusyChangeRef.current?.(true));
      map.on('popupclose', () => onMapBusyChangeRef.current?.(false));

      const googleResult = await addGoogleFleetBasemap(map);
      if (cancelled) {
        map.remove();
        return;
      }

      if (!googleResult.ok) {
        const raster = getLeafletRasterBasemapOptions();
        L.tileLayer(raster.url, {
          ...raster.options,
          maxZoom: FLEET_MAX_ZOOM,
          bounds: FLEET_SINGAPORE_BOUNDS,
        }).addTo(map);
        setBasemapKind('raster');
        setBasemapError(getGoogleMapsApiKey() ? googleResult.error : null);
      } else {
        setBasemapKind('google');
        setBasemapError(null);
      }

      L.control.zoom({ position: 'bottomright' }).addTo(map);
      mapRef.current = map;
      window.setTimeout(() => map.invalidateSize(), 120);
    })();

    return () => {
      cancelled = true;
      mapRef.current?.remove();
      mapRef.current = null;
      setBasemapKind('loading');
      setBasemapError(null);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    renderVehicleLayers(
      map,
      layersRef.current,
      vehicles,
      selectedRegistration,
      showLiveVehicles,
      (registration) => onSelectVehicleRef.current?.(registration),
    );
  }, [vehicles, selectedRegistration, showLiveVehicles]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const vehiclesNow = vehiclesRef.current;
    const layers = layersRef.current;
    layers.routeArrows.forEach((marker) => map.removeLayer(marker));
    layers.routeArrows = [];
    layers.stopMarkers.forEach((marker) => map.removeLayer(marker));
    layers.stopMarkers = [];
    layers.routes.forEach((line) => map.removeLayer(line));
    layers.routes = [];
    if (layers.start) map.removeLayer(layers.start);
    layers.start = undefined;
    if (layers.end) map.removeLayer(layers.end);
    layers.end = undefined;
    if (layers.parked) map.removeLayer(layers.parked);
    layers.parked = undefined;

    const validLayers = routeLayers
      .map((layer) => ({
        estimated: layer.estimated,
        points: layer.points.filter(
          (point) =>
            Number.isFinite(point.lat) &&
            Number.isFinite(point.lng) &&
            Math.abs(point.lat) <= 90 &&
            Math.abs(point.lng) <= 180,
        ),
      }))
      .filter((layer) => layer.points.length >= 2);
    const routeFocus = validLayers.length > 0;
    const bounds: L.LatLngTuple[] = [];
    const plotted = showLiveVehicles
      ? vehiclesNow.filter((vehicle) => vehicle.lat != null && vehicle.lng != null)
      : [];

    plotted.forEach((vehicle) => {
      const selected = selectedRegistration === vehicle.registration;
      if (!routeFocus || selected) {
        bounds.push([vehicle.lat!, vehicle.lng!]);
      }
    });

    if (validLayers.length > 0) {
      validLayers.forEach((layer) => {
        const line = layer.points.map((point) => [point.lat, point.lng] as L.LatLngTuple);
        const polyline = L.polyline(line, {
          pane: 'fleetRoutePane',
          color: '#c41e3a',
          weight: layer.estimated ? 4 : 5,
          opacity: layer.estimated ? 0.72 : 0.92,
          dashArray: layer.estimated ? '10 8' : undefined,
          lineCap: 'round',
          lineJoin: 'round',
        }).addTo(map);
        layers.routes.push(polyline);
        addRouteArrows(map, line, layer.estimated, layers.routeArrows);
        line.forEach((point) => bounds.push(point));
      });

      const gpsLayers = validLayers.filter((layer) => !layer.estimated);
      const firstLayer = gpsLayers[0] ?? validLayers[0]!;
      const lastLayer = gpsLayers[gpsLayers.length - 1] ?? validLayers[validLayers.length - 1]!;
      const first = firstLayer.points[0]!;
      const last = lastLayer.points[lastLayer.points.length - 1]!;

      layers.start = L.marker([first.lat, first.lng], {
        pane: 'fleetMarkerPane',
        icon: endpointMarkerIcon('S', '#16a34a', '#15803d'),
        zIndexOffset: 400,
      }).addTo(map);
      bindFleetPopup(layers.start, 'Route start', [
        first.at ? formatRouteTimeOnly(first.at) : '—',
      ]);

      layers.end = L.marker([last.lat, last.lng], {
        pane: 'fleetMarkerPane',
        icon: endpointMarkerIcon('E', '#dc2626', '#b91c1c'),
        zIndexOffset: 400,
      }).addTo(map);
      bindFleetPopup(layers.end, 'Route end', [
        last.at ? formatRouteTimeOnly(last.at) : '—',
      ]);
    } else if (routeLayers.length === 1 && routeLayers[0]?.points.length === 1) {
      const only = routeLayers[0]!.points[0]!;
      layers.parked = L.circle([only.lat, only.lng], {
        pane: 'fleetRoutePane',
        radius: 80,
        color: '#c41e3a',
        fillColor: '#c41e3a',
        fillOpacity: 0.12,
        weight: 2,
      }).addTo(map);
      layers.start = L.marker([only.lat, only.lng], {
        pane: 'fleetMarkerPane',
        icon: endpointMarkerIcon('P', '#c41e3a', '#991b1b'),
      }).addTo(map);
      bounds.push([only.lat, only.lng]);
    }

    routeStops.forEach((stop, index) => {
      const lines = [
        `${stop.durationMinutes} min`,
        ...(stop.address ? [stop.address] : []),
        `${formatRouteTimeOnly(stop.startedAt)} – ${formatRouteTimeOnly(stop.endedAt)}`,
      ];
      const marker = L.marker([stop.lat, stop.lng], {
        pane: 'fleetMarkerPane',
        icon: stopMarkerIcon(String(index + 1)),
        zIndexOffset: 600,
      }).addTo(map);
      bindFleetStopPopup(
        marker,
        index + 1,
        lines,
        routeDate ? fleetTasksForStop(tasks, routeDate, stop, taskGeoCoords) : [],
        tasks,
        clients,
        canManageTasks,
        () => onStopAddTaskRef.current?.(stop),
        () => onStopAssignTaskRef.current?.(stop),
        (task) => onStopTaskClickRef.current?.(task),
      );
      layers.stopMarkers.push(marker);
      bounds.push([stop.lat, stop.lng]);
    });

    const fitKey = [
      selectedRegistration ?? '',
      routeDate,
      routeStops.map((stop) => `${stop.startedAt}|${stop.endedAt}`).join(','),
      routeLayers.map((layer) => `${layer.estimated ? 'e' : 'g'}:${layer.points.length}`).join('|'),
      liveFleetReady ? 'live' : 'empty',
    ].join('::');

    if (bounds.length === 0) {
      if (fitKey !== lastFitKeyRef.current) {
        map.setView([1.3521, 103.8198], 12, { animate: false });
        lastFitKeyRef.current = fitKey;
      }
      return;
    }

    if (fitKey === lastFitKeyRef.current) return;
    lastFitKeyRef.current = fitKey;

    const boundsObj = L.latLngBounds(bounds);
    const clamped = FLEET_SINGAPORE_BOUNDS.intersects(boundsObj)
      ? boundsObj.pad(0.02)
      : FLEET_SINGAPORE_BOUNDS;
    map.fitBounds(clamped, {
      padding: routeFocus ? [20, 20] : [36, 36],
      maxZoom: routeFocus ? 17 : 14,
      animate: false,
    });
  }, [
    selectedRegistration,
    routeLayers,
    routeStops,
    routeDate,
    tasks,
    clients,
    taskGeoCoords,
    showLiveVehicles,
    canManageTasks,
    liveFleetReady,
  ]);

  return (
    <div
      className={[
        'fleet-map',
        basemapKind === 'google' ? 'fleet-map--google' : '',
        basemapKind === 'raster' ? 'fleet-map--raster' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div ref={containerRef} className="fleet-map__canvas" aria-label="Fleet map" />
      {basemapKind === 'raster' && basemapError ? (
        <p className="fleet-map__basemap-hint">
          Google Maps unavailable ({basemapError}). Using raster tiles — check Maps JavaScript API and
          key restrictions for <code>portal.banteckhan.com.sg</code>.
        </p>
      ) : null}
      {vehicles.length === 0 ? (
        <div className="fleet-map__overlay">No vehicle locations yet.</div>
      ) : null}
    </div>
  );
}
